import { Fragment, memo, useReducer } from 'react';

import { isEmpty, isNonNullish } from 'remeda';
import useSWR from 'swr';

import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import { FileRow } from '../fileRow/FileRow';
import { UserDateColumn } from '../fileSystemTableColumn/userDateColumn/UserDateColumn';

import { FolderActionsColumn } from './folderRowColumn/folderActionsColumn/FolderActionsColumn';
import { FolderNameColumn } from './folderRowColumn/folderNameColumn/FolderNameColumn';

import { fileSystemService } from 'v2/api/fileSystemService';
import { EmptyPlaceholder } from 'v2/components/emptyPlaceholder/EmptyPlaceholder';
import { Loader } from 'v2/components/loader/Loader';
import { FolderModel } from 'v2/types/FileSystemModel';

interface FolderRowProps {
  folder: FolderModel;
  depth: number;
  onRevalidate?: VoidFunction;
}

export const FolderRow = memo<FolderRowProps>(({ folder, depth, onRevalidate }) => {
  const [isExpanded, setExpanded] = useReducer((state) => !state, false);

  const { data: childFolders, isLoading: isLoadingChildFolders } = useSWR(
    isExpanded && ['getChildFolders', folder.id],
    fileSystemService.getChildFolders
  );

  const {
    data: childFiles,
    isLoading: isLoadingChildFiles,
    mutate: mutateChildFiles,
  } = useSWR(isExpanded && ['getChildFiles', folder.id], fileSystemService.getChildFiles);

  return (
    <Fragment>
      <TableRow>
        <FolderNameColumn name={folder.name} isExpanded={isExpanded} onExpand={setExpanded} depth={depth} />

        <UserDateColumn user={folder.createdBy} date={folder.createdDate} />

        <UserDateColumn user={folder.modifiedBy} date={folder.modifiedDate} />

        <FolderActionsColumn folder={folder} onRevalidate={onRevalidate} />
      </TableRow>

      {isExpanded && (
        <Fragment>
          {isLoadingChildFolders || isLoadingChildFiles ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <Loader />
              </TableCell>
            </TableRow>
          ) : (
            isNonNullish(childFolders) &&
            isNonNullish(childFiles) && (
              <Fragment>
                {isEmpty(childFolders) && isEmpty(childFiles) ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <EmptyPlaceholder />
                    </TableCell>
                  </TableRow>
                ) : (
                  <Fragment>
                    {childFolders.map((folder) => (
                      <FolderRow key={folder.id} folder={folder} depth={depth + 1} onRevalidate={onRevalidate} />
                    ))}

                    {childFiles.map((file) => (
                      <FileRow key={file.id} file={file} depth={depth + 1} onRevalidate={mutateChildFiles} />
                    ))}
                  </Fragment>
                )}
              </Fragment>
            )
          )}
        </Fragment>
      )}
    </Fragment>
  );
});

FolderRow.displayName = 'FolderRow';
